import { BO, BusinessObjectBase, CollectionChangedJoinPoint, FeatureFlagsRulesServiceBase, ObservableCollection, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { AddressModel, DocumentClientModel, ExchangeRateModel, SaleDocumentItemModel, SaleProFormaModel, VatTableItemModel } from '@skylab-shared/dataModel';
import { substractDays } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { ContextKeys } from '../common/context-keys';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { CompanyParametersQuery } from '../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../currency/queries/currency.query-base';
import { DocumentExchangeRateValidationBoPart } from '../documents/bo-parts/document-exchange-rate-validation.bo-part';
import { DocumentItemsNumberingBoPart } from '../documents/bo-parts/document-items-numbering.bo-part';
import { DocumentNumberBoPart } from '../documents/bo-parts/document-number.bo-part';
import { DocumentVatRateForSaleValidationBoPart } from '../documents/bo-parts/document-vat-rate-for-sale-validation.bo-part';
import { DocumentWstoBoPart } from '../documents/bo-parts/document-wsto.bo-part';
import { CommunityTransactionForSaleMixin } from '../documents/mixins/community-transaction-for-sale.mixin';
import { ExchangeRateMixin } from '../documents/mixins/exchange-rate.mixin';
import { ToPayMixin } from '../documents/mixins/to-pay.mixin';
import { NumberingQuery } from '../numbering/queries/numbering.query-base';
import { PaymentMethodQuery } from '../payment-method/queries/payment-method.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { BusinessDayCalculatorPL } from '../utils/business-days-calculator';
import { collectionChangedJoinPointFilter } from '../utils/collection-changed-filter';
import { VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { BankAccountNumberMixin } from './mixins/bank-account.mixin';
import { ClientMixin } from './mixins/client.mixin';
import { SaleLinksMixin } from './mixins/sale-links.mixin';
import { SaleDocumentCalculatorBoPart } from './parts/sale-document-calculator.bo-part';
import { SaleDocumentClientBoPart } from './parts/sale-document-client.bo-part';
import { SaleDocumentItemsValidationsBoPart } from './parts/sale-document-items-validations.bo-part';
import { SaleDocumentJpkGroupsBoPart } from './parts/sale-document-jpk-groups.bo-part';
import { SaleDocumentPaymentsBoPart } from './parts/sale-document-payment.bo-part';
import { SaleDocumentSplitPaymentBoPart } from './parts/sale-document-split-payment.bo-part';
import { SaleDocumentVatReleaseBoPart } from './parts/sale-document-vat-release.bo-part';
import { SaleDocumentWithDueDateBoPart } from './parts/sale-document-with-due-date.bo-part';
import { SaleKindQuery } from './queries/sale-kind.query-base';
import { refreshSaleDocumentItemPriceAsync } from './utils/sale-utils';

export interface SaleProFormaBO extends ClientMixin<SaleProFormaModel>,ExchangeRateMixin<SaleProFormaModel>, CommunityTransactionForSaleMixin<SaleProFormaModel>, ToPayMixin<SaleProFormaModel>, SaleLinksMixin<SaleProFormaModel>, BankAccountNumberMixin<SaleProFormaModel> {};

@BO({
    modelType: SaleProFormaModel,
    mixins: [ExchangeRateMixin, CommunityTransactionForSaleMixin, ToPayMixin, SaleLinksMixin, BankAccountNumberMixin, ClientMixin]
})
export class SaleProFormaBO extends BusinessObjectBase<SaleProFormaModel> {
    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepositoryContract,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected readonly clientRepository: ClientRepositoryBase,
        protected readonly queryBus: QueryBus
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(SaleKindQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatReleaseReasonQuery),
            this.localContext.preload(ContextKeys.company)
        ]);

        if (this.model.isPersistent) {
            await this.loadSourceClient();
            await this.loadLinksAsync();
        }

        this.registerCanExecuteRules();
        this.registerReadonlyRules();
        this.registerPropretyChanged();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new SaleDocumentItemsValidationsBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new SaleDocumentCalculatorBoPart(this.weaver),
            new SaleDocumentWithDueDateBoPart(this.weaver),
            new SaleDocumentPaymentsBoPart(this.weaver),
            new SaleDocumentClientBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentSplitPaymentBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService),
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new SaleDocumentJpkGroupsBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentVatReleaseBoPart(this.weaver, this.queryBus),
            new DocumentExchangeRateValidationBoPart(this.weaver),
        ]);
    }

    private registerCanExecuteRules(): void {
        this.canExecute.addRule(
            [[ObservableCollection, 'add'], [ObservableCollection, 'remove']],
            ctx => ctx.target === ctx.bo.model.items ? !(this.linksOrderedProForma) : true
        );
    }

    private registerReadonlyRules(): void {
        this.readonly.setAsReadonly([SaleProFormaModel, 'inventoryEffect']);

        this.readonly.addRule(
            [[SaleProFormaModel, 'kind']],
            () => this.model.isPersistent
        );

        this.readonly.setReadonlyOnSubtree(DocumentClientModel, () => this.linksOrderedProForma);
        this.readonly.setReadonlyOnSubtree(AddressModel, () => this.linksOrderedProForma);
        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => this.linksOrderedProForma);
        this.readonly.setReadonlyOnSubtree(SaleDocumentItemModel, () => this.linksOrderedProForma, {
            exceptions: [
                [SaleDocumentItemModel, <keyof SaleDocumentItemModel>'!e'],
            ]
        });
        this.readonly.setReadonlyOnSubtree(VatTableItemModel, () => this.linksOrderedProForma);

        this.readonly.addRule([
            [SaleProFormaModel, 'bankAccountNumberId'],
            [SaleProFormaModel, 'baseCurrencyBankAccountNumberId'],
            [SaleProFormaModel, 'buyer'],
            [SaleProFormaModel, 'calculationMethod'],
            [SaleProFormaModel, 'cashBasis'],
            [SaleProFormaModel, 'client'],
            [SaleProFormaModel, 'communityTransactionId'],
            [SaleProFormaModel, 'currencyId'],
            [SaleProFormaModel, 'deliveryAddress'],
            [SaleProFormaModel, 'dueDate'],
            [SaleProFormaModel, 'dueDateInDays'],
            [SaleProFormaModel, 'exchangeRate'],
            [SaleProFormaModel, 'grossValue'],
            [SaleProFormaModel, 'issueDate'],
            [SaleProFormaModel, 'issuedByVatPayer'],
            [SaleProFormaModel, 'issuer'],
            [SaleProFormaModel, 'items'],
            [SaleProFormaModel, 'kind'],
            [SaleProFormaModel, 'myCompanyId'],
            [SaleProFormaModel, 'netValue'],
            [SaleProFormaModel, 'documentNumber'],
            [SaleProFormaModel, 'orderDate'],
            [SaleProFormaModel, 'paymentMethodId'],
            [SaleProFormaModel, 'recipient'],
            [SaleProFormaModel, 'vatTableItems'],
            [SaleProFormaModel, 'vatTableItemsInBaseCurrency'],
            [SaleProFormaModel, 'numberingId'],
            [SaleProFormaModel, 'jpkVatGroups'],
            [SaleProFormaModel, 'splitPayment'],
            [SaleProFormaModel, 'customDueDateDescription'],
            [SaleProFormaModel, 'wstoConsumerCountryId'],
            [SaleProFormaModel, 'wstoShipmentCountryId'],
            [SaleProFormaModel, 'vatReleaseReasonCustomName'],
            [SaleProFormaModel, 'vatReleaseReasonSource'],
            [SaleProFormaModel, 'vatReleaseReasonId'],
            [SaleProFormaModel, 'transactionLocation'],
        ], () => this.linksOrderedProForma);
    }

    private registerPropretyChanged(): void {
        this.updateBankAccountsNumbers();
        this.applyDiscountToNewItemOnCollectionChanged();
    }

    private updateBankAccountsNumbers(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleProFormaModel, ['currencyId', 'communityTransactionId', 'paymentMethodId'])(
            _ => this.updateBankAccountNumber()
        )));
    }

    private applyDiscountToNewItemOnCollectionChanged(): void {
        const reactToDocumentItemsCollectionChange = collectionChangedJoinPointFilter(this)(this.model.items);
        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, reactToDocumentItemsCollectionChange(
            ctx => (ctx.newItems as SaleDocumentItemModel[]).forEach((item: SaleDocumentItemModel) => this.applyClientDiscountToItem(item))
        )));
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.issueDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshSaleDocumentItemPriceAsync(item, this, this.productRepository)));
    }
}
